import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid2,
  LinearProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme
} from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { useSelector } from "../../app/helpers";
import type { RootState } from "../../app/rootReducer";
import type { Weekdays } from "../../components/Pickers/WeekdayPicker/weekday-picker.component";
import { officeChartAccentColor } from "../Dashboard2/utils/dashboard2-status-display.utils";
import { ManagementDetailPageHeader } from "./components/ManagementDetailPageHeader";
import { ManagementDisplayUnitToggle } from "./components/ManagementDisplayUnitToggle";
import { ManagementChartEmptyState } from "./components/ManagementChartEmptyState";
import { ManagementFilterSelect } from "./components/ManagementFilterSelect";
import { ManagementLoadErrorAlert } from "./components/ManagementLoadErrorAlert";
import { ManagementStatusPeriodTooltip } from "./components/ManagementStatusPeriodTooltip";
import { ManagementWeekdaysToggle } from "./components/ManagementWeekdaysToggle";
import { ManagementWorkStatusDateRange } from "./components/ManagementWorkStatusDateRange";
import { useRemoteFetchManagementDashboard } from "./hooks/useRemoteFetchManagementDashboard";
import { useManagementPrint } from "./hooks/useManagementPrint";
import type { ManagementDisplayUnit } from "./typings/management-display-unit";
import {
  getManagementChartLegendProps,
  getManagementNeutralChartFill
} from "./utils/management-chart-theme.utils";
import { getManagementPeriodChartYAxisProps } from "./utils/management-display-unit.utils";
import { MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX } from "./utils/management-toolbar.constants";
import {
  clampManagementWeekdaysToCompanySettings,
  getManagementInitialWeekdays
} from "./utils/management-period-weekend.utils";
import {
  aggregateStatusesByWeekday,
  aggregateStatusesOverTime,
  buildManagementUserStatusQueryParams,
  getUserStatusOverTimeChartRows,
  getUserStatusWeekdayChartRows,
  isManagementStatusMonthGrainAvailable,
  MANAGEMENT_STATUS_SERIES_KEYS,
  mapStatusesPeriodDaysForUserStatusDetail,
  type ManagementStatusGroupMode
} from "./utils/management-user-status-detail.utils";

const STATUS_LABEL_KEYS = {
  office: "_management_statusOffice",
  mobileWork: "_management_statusMobile",
  notSpecified: "_management_statusNotSpecified"
} as const;

/**
 * Management linked report: user status detail (management-dashboard status series).
 */
const ManagementUserStatusDetailPage: FunctionComponent = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const bookingsOnSaturdaysAllowed = useSelector(
    (state: RootState) => state.login.userInformation.company?.meta?.bookingsOnSaturdaysAllowed
  );
  const bookingsOnSundaysAllowed = useSelector(
    (state: RootState) => state.login.userInformation.company?.meta?.bookingsOnSundaysAllowed
  );

  const [rangeStart, setRangeStart] = useState(() => DateTime.now().startOf("month"));
  const [rangeEnd, setRangeEnd] = useState(() => DateTime.now().startOf("day"));
  const [displayUnit, setDisplayUnit] = useState<ManagementDisplayUnit>("percent");
  const [weekdays, setWeekdays] = useState<Weekdays>(() =>
    getManagementInitialWeekdays({
      bookingsOnSaturdaysAllowed,
      bookingsOnSundaysAllowed
    })
  );
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [groupMode, setGroupMode] = useState<ManagementStatusGroupMode>("day");

  const monthGrainAvailable = isManagementStatusMonthGrainAvailable(rangeStart, rangeEnd);
  const { contentRef, handlePrint } = useManagementPrint(t("_management_userStatusDetailTitle"));

  useEffect(() => {
    if (groupMode === "month" && !monthGrainAvailable) {
      setGroupMode("day");
    }
  }, [groupMode, monthGrainAvailable]);

  useEffect(() => {
    setWeekdays(prev =>
      clampManagementWeekdaysToCompanySettings(prev, {
        bookingsOnSaturdaysAllowed,
        bookingsOnSundaysAllowed
      })
    );
  }, [bookingsOnSaturdaysAllowed, bookingsOnSundaysAllowed]);

  const queryParams = useMemo(
    () =>
      buildManagementUserStatusQueryParams({
        rangeStart,
        rangeEnd,
        departmentIds
      }),
    [departmentIds, rangeEnd, rangeStart]
  );

  const { data, isLoading, isError, isFetching, refetch } =
    useRemoteFetchManagementDashboard(queryParams);
  const showInitialLoading = isLoading && !data;
  const showRefetchProgress = isFetching && Boolean(data);

  const weekendSettings = useMemo(
    () => ({ bookingsOnSaturdaysAllowed, bookingsOnSundaysAllowed }),
    [bookingsOnSaturdaysAllowed, bookingsOnSundaysAllowed]
  );

  const statusDays = useMemo(() => {
    if (!data?.statusesPeriodDays) {
      return [];
    }
    return mapStatusesPeriodDaysForUserStatusDetail(
      data.statusesPeriodDays,
      weekdays,
      weekendSettings,
      i18n.language
    );
  }, [data?.statusesPeriodDays, i18n.language, weekdays, weekendSettings]);

  const overTimeData = useMemo(
    () =>
      getUserStatusOverTimeChartRows(
        aggregateStatusesOverTime(statusDays, groupMode, i18n.language),
        displayUnit
      ),
    [displayUnit, groupMode, i18n.language, statusDays]
  );

  const weekdayData = useMemo(
    () => getUserStatusWeekdayChartRows(aggregateStatusesByWeekday(statusDays), displayUnit),
    [displayUnit, statusDays]
  );

  /** Keep labels stable while the dashboard query refetches (queryKey change clears `data`). */
  const [departmentOptions, setDepartmentOptions] = useState<Array<{ id: string; name: string }>>(
    []
  );

  useEffect(() => {
    const nextDepartments = data?.departments;
    if (!nextDepartments?.length) {
      return;
    }
    setDepartmentOptions(prev => {
      const byId = new Map(prev.map(dep => [dep.id, dep]));
      for (const dep of nextDepartments) {
        byId.set(String(dep.id), { id: String(dep.id), name: dep.name });
      }
      return [...byId.values()];
    });
  }, [data?.departments]);

  const statusColors = {
    office: officeChartAccentColor(theme),
    mobileWork: theme.palette.info.main,
    notSpecified: getManagementNeutralChartFill(theme)
  };
  const chartAxisTick = { fontSize: 11, fill: theme.palette.text.secondary };
  const periodChartYAxis = getManagementPeriodChartYAxisProps(displayUnit);
  const legendProps = getManagementChartLegendProps(theme);

  const handleRangeChange = (nextStart: DateTime, nextEnd: DateTime) => {
    setRangeStart(nextStart);
    setRangeEnd(nextEnd);
  };

  return (
    <Box ref={contentRef} data-testid="management-user-status-detail-page" sx={{ width: "100%" }}>
      <ManagementDetailPageHeader
        title={t("_management_userStatusDetailTitle")}
        onPrint={handlePrint}
      />

      <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px", mb: 2 }}>
        <Grid2 container spacing={2} alignItems="flex-end">
          <Grid2 size={{ xs: 12, md: 4 }}>
            <ManagementWorkStatusDateRange
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              onChange={handleRangeChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <ManagementFilterSelect
              multiple
              label={t("Department")}
              labelId="management-user-dept-label"
              options={departmentOptions}
              allLabel={t("All departments")}
              value={departmentIds}
              onChange={setDepartmentIds}
              data-testid="management-user-department-select"
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <ManagementWeekdaysToggle weekdays={weekdays} onChange={setWeekdays} />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <ManagementDisplayUnitToggle value={displayUnit} onChange={setDisplayUnit} showLabel />
          </Grid2>
        </Grid2>
      </Paper>

      {showInitialLoading && (
        <Box
          data-testid="management-user-status-loading"
          sx={{ display: "flex", justifyContent: "center", py: 6 }}
        >
          <CircularProgress aria-label={t("_management_loading")} />
        </Box>
      )}

      {isError && !showInitialLoading && (
        <ManagementLoadErrorAlert
          messageKey="_management_loadError"
          onRetry={() => {
            refetch().catch(() => undefined);
          }}
          testId="management-user-status-error"
        />
      )}

      {!showInitialLoading && !isError && data && (
        <Stack spacing={2}>
          {showRefetchProgress && (
            <LinearProgress
              data-testid="management-user-status-refetch-progress"
              aria-label={t("_management_loading")}
              sx={{ borderRadius: 1 }}
            />
          )}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 600 }}>
                {t("_management_statusOverTime")}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={groupMode}
                aria-label={t("_management_statusGroupMode")}
                onChange={(_, next: ManagementStatusGroupMode | null) => {
                  if (next === "month" && !monthGrainAvailable) {
                    return;
                  }
                  if (next) {
                    setGroupMode(next);
                  }
                }}
                data-testid="management-status-group-mode"
                sx={{
                  "& .MuiToggleButton-root": {
                    height: MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX
                  }
                }}
              >
                <ToggleButton value="day">{t("Day")}</ToggleButton>
                <ToggleButton value="week">{t("Week")}</ToggleButton>
                <ToggleButton value="month" disabled={!monthGrainAvailable}>
                  {t("Month")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Box
              sx={{ width: "100%", height: 280 }}
              data-testid="management-status-over-time-chart"
            >
              {overTimeData.length === 0 ? (
                <ManagementChartEmptyState />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={overTimeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="label" tick={chartAxisTick} />
                    <YAxis tick={chartAxisTick} {...periodChartYAxis} />
                    <Tooltip
                      content={
                        <ManagementStatusPeriodTooltip
                          displayUnit={displayUnit}
                          notSpecifiedDataKey="notSpecified"
                        />
                      }
                    />
                    <Legend
                      wrapperStyle={legendProps.wrapperStyle}
                      formatter={legendProps.formatter}
                    />
                    {MANAGEMENT_STATUS_SERIES_KEYS.map(key => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        name={t(STATUS_LABEL_KEYS[key])}
                        stroke={statusColors[key]}
                        strokeWidth={2}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px" }}>
            <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
              {t("_management_statusByWeekday")}
            </Typography>
            <Box
              sx={{ width: "100%", height: 280 }}
              data-testid="management-status-by-weekday-chart"
            >
              {weekdayData.length === 0 ? (
                <ManagementChartEmptyState />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weekdayData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="weekday"
                      tick={chartAxisTick}
                      tickFormatter={value => t(String(value))}
                    />
                    <YAxis tick={chartAxisTick} {...periodChartYAxis} />
                    <Tooltip
                      content={
                        <ManagementStatusPeriodTooltip
                          displayUnit={displayUnit}
                          notSpecifiedDataKey="notSpecified"
                        />
                      }
                    />
                    <Legend
                      wrapperStyle={legendProps.wrapperStyle}
                      formatter={legendProps.formatter}
                    />
                    {MANAGEMENT_STATUS_SERIES_KEYS.map(key => (
                      <Bar
                        key={key}
                        dataKey={key}
                        name={t(STATUS_LABEL_KEYS[key])}
                        fill={statusColors[key]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Stack>
      )}
    </Box>
  );
};

export default ManagementUserStatusDetailPage;
